"use client";

import {
  type ComponentProps,
  type MouseEvent,
  startTransition,
  useCallback,
  useMemo,
} from "react";

import { trackViewTransition } from "@/lib/view-transitions/click-through";
import { useSetFinishViewTransition } from "@/lib/view-transitions/view-transitions";

import { IntlLink, useIntlRouter } from "./navigation-base";

// =============================================================================

type IntlRouter = ReturnType<typeof useIntlRouter>;
type Href = Parameters<IntlRouter["push"]>[0];
type NavigateOptions = Parameters<IntlRouter["push"]>[1];

// Same rules as next/link: let the browser handle new tabs, downloads…
function shouldPreserveDefault(event: MouseEvent<HTMLAnchorElement>): boolean {
  const target = event.currentTarget.getAttribute("target");
  return Boolean(
    (target && target !== "_self") ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button === 1
  );
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// =============================================================================

/**
 * next-intl router whose push/replace run inside a View Transition.
 */
export function useRouter(): IntlRouter {
  const router = useIntlRouter();
  const finishViewTransition = useSetFinishViewTransition();

  const withTransition = useCallback(
    (navigate: () => void) => {
      if (!("startViewTransition" in document) || prefersReducedMotion()) {
        navigate();
        return;
      }

      trackViewTransition(
        document.startViewTransition(
          () =>
            new Promise<void>((resolve) => {
              startTransition(() => {
                navigate();
                finishViewTransition(() => resolve());
              });
            })
        )
      );
    },
    [finishViewTransition]
  );

  const push = useCallback(
    (href: Href, options?: NavigateOptions) =>
      withTransition(() => router.push(href, options)),
    [router, withTransition]
  );

  const replace = useCallback(
    (href: Href, options?: NavigateOptions) =>
      withTransition(() => router.replace(href, options)),
    [router, withTransition]
  );

  return useMemo(() => ({ ...router, push, replace }), [router, push, replace]);
}

// =============================================================================

export type LinkProps = ComponentProps<typeof IntlLink>;

/**
 * Locale-aware link that navigates with a View Transition.
 */
export function Link({
  href,
  locale,
  replace,
  scroll,
  onClick,
  ...props
}: LinkProps) {
  const router = useRouter();

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) return;
      if (!("startViewTransition" in document)) return;
      if (shouldPreserveDefault(event)) return;
      if (typeof href !== "string" || href.startsWith("#")) return;

      event.preventDefault();
      const navigate = replace ? router.replace : router.push;
      navigate(href, { locale, scroll: scroll ?? true });
    },
    [onClick, href, locale, replace, scroll, router]
  );

  return (
    <IntlLink
      href={href}
      locale={locale}
      replace={replace}
      scroll={scroll}
      onClick={handleClick}
      {...props}
    />
  );
}

// =============================================================================
